import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import isTouchDevice from "@insite/client-framework/Common/Utilities/isTouchDevice";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import { HasCategoryContext } from "@insite/client-framework/Components/CategoryContext";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSelectedBrandPath, getSelectedCategoryPath } from "@insite/client-framework/Store/Context/ContextSelectors";
import { BrandStateContext, getBrandsDataView } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import loadBrands from "@insite/client-framework/Store/Data/Brands/Handlers/LoadBrands";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { useGetLink, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import {
    getProductListDataView,
    getProductListDataViewProperty,
} from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductCarouselProductStyles } from "@insite/content-library/Components/ProductCarouselProduct";
import FlexWrapContainer, { FlexWrapContainerProps } from "@insite/content-library/Widgets/Brand/FlexWrapContainer";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps, LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ArrowRightIcon from "@tCentro/Icons/ArrowRightIcon";
import ArrowRightLongIcon from "@tCentro/Icons/ArrowRightLongIcon";
import { LinkModel } from "@tCentro/Store/CustomLinksSelectors";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import useEmblaCarousel from "embla-carousel-react";
import parse from "html-react-parser";
import isMobile from "ismobilejs";
import truncate from "lodash/truncate";
import React, { FC, useCallback, useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    maxNumberOfColumns = "maxNumberOfColumns",
    mainTitle = "mainTitle",
    seeAllLinkLabel = "seeAllLinkLabel",
    seeAllLink = "seeAllLink",
    industryLinkLabel = "industryLinkLabel",
    industryImg = "industryImg",
    industryTitle = "industryTitle",
    industryDescription = "industryDescription",
    industriesCards = "industriesCards",
}

interface IndustriesList {
    industryTitle: string;
    industryLink: LinkFieldValue;
    industryDescription: string;
}

interface IndustriesCardsModel {
    fields: IndustriesList;
}
interface OwnProps extends WidgetProps {
    fields: {
        [fields.maxNumberOfColumns]: number;
        [fields.industriesCards]: IndustriesCardsModel[];
        [fields.industryTitle]: string;
        [fields.seeAllLink]: LinkFieldValue;
        [fields.seeAllLinkLabel]: string;
        [fields.industryLinkLabel]: string;
        [fields.mainTitle]: string;
        [fields.industryImg]: string;
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: { categoryId: string }) => {
    const location = getLocation(state);
    const categoryPath =
        getSelectedCategoryPath(state) ||
        (location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname);
    const catalogPageState = getCatalogPageStateByPath(state, categoryPath);
    const { pathname } = getLocation(state);
    const brandPath = getSelectedBrandPath(state) || pathname;
    const { productFilters } = state.pages.productList;

    return {
        catalogPageState,
        query: location.search,
        productsDataView: getProductListDataView(state),
        brandPath,
        category: getCategoryState(state, ownProps.categoryId).value,
        productFiltersQuery: productFilters.query,
    };
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme> &
    HasShellContext &
    HasCategoryContext;

const mapDispatchToProps = {};

export interface BrandListStyles {
    infoCardTitle?: TypographyProps;
    container?: InjectableCss;
    wrapper?: InjectableCss;
    imageContainer?: FlexWrapContainerProps;
    imglinkStyles?: InjectableCss;
    image?: LazyImageProps;
    mainContainer?: GridContainerProps;
    prevArrowGridItem?: GridItemProps;
    prevArrowButton?: ButtonPresentationProps;
    nextArrowGridItem?: GridItemProps;
    nextArrowButton?: ButtonPresentationProps;
    carouselGridItem?: GridItemProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    buttonLink?: LinkPresentationProps;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    carouselProductStyles?: ProductCarouselProductStyles;
    subcategoryNameStyles?: TypographyProps;
    subcategoryDescriptionStyles?: InjectableCss;
    subCategoryContentContainer: InjectableCss;
    categoryNameTitleStyle?: TypographyProps;
    dflex?: InjectableCss;
    titleContainerStyles?: InjectableCss;
    rightIconStyles?: IconPresentationProps;
    linkContainerStyles?: InjectableCss;
    readMoreStyles?: InjectableCss;
    linkStyles?: InjectableCss;
    topMainContainer?: InjectableCss;
}

export const listStyles: BrandListStyles = {
    topMainContainer: {
        css: css`
            margin: 64px 0px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin: 40px 0px;
                        padding-left: 16px;
                        padding-right: 16px;
                    `,
                    css`
                        margin: 40px 0px;
                        padding-left: 16px;
                        padding-right: 16px;
                    `,
                    css`
                        margin: 40px 0px;
                        padding-left: 16px;
                        padding-right: 16px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    linkStyles: {
        css: css`
            > span {
                font-family: ${TYPOGRAPHY.fontFamily.title};
                font-size: ${TYPOGRAPHY.size.large};
                font-weight: ${TYPOGRAPHY.weight.mediumbold};
                line-height: ${TYPOGRAPHY.lineHeight.base};
            }
        `,
    },
    readMoreStyles: {
        css: css`
            span {
                font-family: ${TYPOGRAPHY.fontFamily.title};
                font-size: ${TYPOGRAPHY.size.smaller};
                font-weight: ${TYPOGRAPHY.weight.mediumbold};
                line-height: 16px;
                text-align: left;
                text-overflow: ellipsis;
                display: -webkit-box;
                -webkit-box-orient: vertical;
                -webkit-line-clamp: 3;
                overflow: hidden;
                min-height: 48px;
            }
        `,
    },

    linkContainerStyles: {
        css: css`
            display: flex;
            align-items: center;
            gap: 4px;
        `,
    },
    titleContainerStyles: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-direction: column;
                        justify-content: center;
                        align-items: center;
                        gap: 16px;
                    `,
                    css`
                        flex-direction: column;
                        justify-content: center;
                        align-items: center;
                        gap: 16px;
                    `,
                    css`
                        flex-direction: row;
                        justify-content: space-between;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    rightIconStyles: {
        css: css`
            height: 11.25px;
            width: 18px;
            margin-left: 5px;
            svg {
                height: 11.25px;
                width: 18px;
                > path {
                    fill: ${getColor("text.link")};
                }
            }
        `,
    },
    dflex: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 4px;
        `,
    },
    categoryNameTitleStyle: {
        variant: "h3",
        css: css`
            //styleName: Headings/LG Page & Screen;
            font-family: ${TYPOGRAPHY.fontFamily.title} !important;
            font-size: 32px !important;
            font-weight: 700 !important;
            line-height: 40px !important;
            text-align: center;
            margin-bottom: 0px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css``,
                        css``,
                        css`
                            font-family: ${TYPOGRAPHY.fontFamily.title} !important;
                            font-size: ${TYPOGRAPHY.size.largest} !important;
                            font-weight: ${TYPOGRAPHY.weight.mediumbold} !important;
                            line-height: 32px !important;
                        `,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    imglinkStyles: {
        css: css`
            width: 100%;
            height: 100%;
        `,
    },
    subCategoryContentContainer: {
        css: css`
            padding: 32px;
            box-sizing: border-box;
            display: flex;
            flex-direction: column;
            gap: 8px;
            margin-bottom: -4px;
            background-color: ${COLORS.background.grayShade};
        `,
    },

    subcategoryNameStyles: {
        variant: "h4",
        as: "h4",
        css: css`
            font-family: ${TYPOGRAPHY.fontFamily.title} !important;
            font-size: 18px !important;
            font-weight: 600 !important;
            line-height: 24px !important;
            text-align: left;
            text-overflow: ellipsis;
            display: -webkit-box;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 3;
            overflow: hidden;
            min-height: 72px;
            margin-bottom: 0px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        min-height: auto;
                        font-size: 18px !important;
                        font-weight: 600 !important;
                        line-height: 24px !important;
                    `,
                    css`
                        min-height: auto;
                        font-size: 18px !important;
                        font-weight: 600 !important;
                        line-height: 24px !important;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    subcategoryDescriptionStyles: {
        css: css`
            -webkit-line-clamp: 4;
            display: -webkit-box;
            -webkit-box-orient: vertical;
            overflow: hidden;
            text-overflow: ellipsis;
            p {
                //styleName: Paragraphs/SM Blurb;
                font-family: ${TYPOGRAPHY.fontFamily.title};
                font-size: 14px;
                font-weight: 400;
                line-height: 20px;
                text-align: left;

                // margin-top: 16px;
                color: ${getProp("theme.typography.p.color")} !important;
            }
        `,
    },
    image: {
        css: css`
            justify-content: flex-start;
            height: 100%;
            span {
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            position: static !important;
                        `,
                        css`
                            position: static !important;
                        `,
                        null,
                        null,
                        null,
                    ])}
            }
            img {
                object-fit: cover;
                width: 100%;
                height: 100%;
            }
        `,
    },
    infoCardTitle: {
        variant: "h2",
        css: css`
            text-align: center;
            font-size: 1.5rem;
            line-height: 33px;
            margin-bottom: 0rem;
            padding-top: 20px;
        `,
    },
    container: {
        css: css`
            margin: 20px 0 20px 0;
            text-align: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding: 0 1rem;
                        `,
                        null,
                        null,
                        css`
                            padding: 0 5rem;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    wrapper: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        max-width: 100% !important;
                        /* padding: 20px; */
                        flex-wrap: wrap;
                        margin-bottom: 0px;
                    `,
                    css`
                        max-width: 100% !important;
                        /* padding: 20px; */
                        flex-wrap: wrap;
                        margin-bottom: 0px;
                    `,
                    css`
                        /* max-width: 720px !important; */
                        flex-wrap: wrap;
                        margin-bottom: 0px;
                    `,
                    css`
                        /* max-width: 917px !important; */
                        padding: 0px;
                        flex-wrap: nowrap;
                    `,
                    css`
                        /* max-width: 1300px !important; */
                        flex-wrap: nowrap;
                        padding: 0px;
                    `,
                ])}
        `,
    },
    mainContainer: {
        gap: 0,
        css: css`
            position: relative;
        `,
    },
    prevArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            position: absolute;
            z-index: 999;
            top: 50%;
            transform: translateY(-50%);
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 12.33% !important;
                        max-width: 12.33% !important;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    prevArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
            background: ${getColor("primary")};
            border-radius: 50%;
            position: relative;
            min-height: auto !important;
            width: 34px;
            height: 34px;
            span {
                width: 100%;
                height: 100%;
                border-radius: 50%;
            }
            svg {
                width: 30px;
                height: 30px;
                stroke: white;
                position: absolute;
                top: 1px;
                left: 0px;
            }
            &:focus {
                outline: none;
            }
        `,
    },
    nextArrowGridItem: {
        width: 1,
        align: "middle",

        css: css`
            position: absolute;
            z-index: 999;
            right: 10px;
            top: 50%;
            transform: translateY(-50%);
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 12.33% !important;
                        max-width: 12.33% !important;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    nextArrowButton: {
        // variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
            background: ${getColor("primary")};
            border-radius: 50%;
            position: relative;
            min-height: auto !important;
            width: 34px;
            height: 34px;
            span {
                width: 100%;
                height: 100%;
                border-radius: 50%;
            }
            svg {
                width: 30px;
                height: 30px;
                stroke: white;
                position: absolute;
                top: 1px;
                left: 3px;
            }
            &:focus {
                outline: none;
            }
            &:hover {
                background: ${getColor("primary")};
            }
        `,
    },
    carouselGridItem: {
        width: 10,
        css: css`
            &:after {
                content: "";
                position: absolute;
                z-index: 1;
                width: 100px;
                box-sizing: border-box;
                right: 0px;
                height: 100%;
                background: linear-gradient(270deg, #ffffff 0%, rgba(255, 255, 255, 0) 100%);
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                ])}
        `,
    },
    carouselWrapper: {
        css: css`
            width: 100%;
            position: relative;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        position: static;
                    `,
                    css`
                        position: static;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        overflow: visible;
                    `,
                    css`
                        overflow: visible;
                    `,
                    null,
                    null,
                    null,
                ])}/* padding: 30px 7px 13px; */
        `,
    },
    carouselSlidesContainer: {
        css: css`
            display: flex;
            gap: 24px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-direction: column;
                    `,
                    css`
                        flex-direction: column;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },

    carouselSlide: {
        css: css`
            padding: 0;
            position: relative;
            flex: 0 0 auto;
            min-width: 290px;
            width: 100%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        min-width: auto;
                    `,
                    css``,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    carouselSlideInner: {
        css: css`
            width: 100%;
            height: 100%;
            display: flex;
            flex-direction: column;
            /* cursor: pointer; */
            position: relative;
            justify-content: flex-start;
            background-color: ${getColor("info.contrast")};
        `,
    },
    imageContainer: {
        css: css`
            min-height: 131px;
            width: 100%;
            span {
                height: 100%;
                width: 100%;
            }
            div[class^="LazyImageStyle"] {
                width: 100%;
            }
            span[class^="IconWrapper"] {
                width: 100%;
                justify-content: center;
            }
        `,
    },
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const styles = listStyles;

const CENHomePageBlogSection: FC<Props> = ({
    fields,
    shellContext,
    theme,
    catalogPageState,
    category,
    query,
    brandPath,
    productsDataView,
    productFiltersQuery,
}) => {
    const titleLink = useGetLink(fields.seeAllLink);
    const { links:industriesLinks } = useGetLinks(fields.industriesCards, o => o.fields.industryLink);
    const [emblaRef, embla] = useEmblaCarousel();
    const [windowResizeTime, setWindowResizeTime] = React.useState(0);
    const [canScrollPrev, setCanScrollPrev] = React.useState(false);
    const [canScrollNext, setCanScrollNext] = React.useState(false);
    const scrollTo = React.useCallback((index: number) => embla?.scrollTo(index), [embla]);
    const scrollPrev = React.useCallback(() => embla?.scrollPrev(), [embla]);
    const scrollNext = React.useCallback(() => embla?.scrollNext(), [embla]);

    const setCanScroll = () => {
        if (shellContext.isInShell) {
            return;
        }
        setCanScrollPrev(!!embla && embla.canScrollPrev());
        setCanScrollNext(!!embla && embla.canScrollNext());
    };

    const getSlidesToScroll = React.useCallback(() => {
        const maxNumberOfColumns = fields.maxNumberOfColumns || 5;
        if (typeof window === "undefined") {
            return maxNumberOfColumns;
        }

        let localSlidesToScroll: number;

        if (window.innerWidth < theme.breakpoints.values[1]) {
            localSlidesToScroll = 1;
        } else if (window.innerWidth < theme.breakpoints.values[2]) {
            localSlidesToScroll = 2;
        } else if (window.innerWidth < theme.breakpoints.values[3]) {
            localSlidesToScroll = 2;
        } else if (window.innerWidth < theme.breakpoints.values[4]) {
            if (window.innerWidth < 1025) {
                localSlidesToScroll = 2;
            } else {
                localSlidesToScroll = 4;
            }
        } else if (window.innerWidth < theme.breakpoints.values[5]) {
            localSlidesToScroll = 5;
        } else {
            localSlidesToScroll = 5;
        }
        return Math.min(localSlidesToScroll, maxNumberOfColumns);
    }, [fields.maxNumberOfColumns]);

    const [slidesToScroll, setSlidesToScroll] = React.useState(getSlidesToScroll());

    const getDraggable = () => {
        if (typeof window === "undefined") {
            return false;
        }

        return window.innerWidth < theme.breakpoints.values[1];
    };

    const [draggable, setDraggable] = React.useState(getDraggable());

    React.useEffect(() => {
        const handleResize = () => {
            setWindowResizeTime(Date.now());
        };

        window.addEventListener("resize", handleResize);
        handleResize();

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    React.useEffect(() => {
        const newSlidesToScroll = getSlidesToScroll();
        setSlidesToScroll(newSlidesToScroll);

        const newDraggable = getDraggable();
        setDraggable(newDraggable);
    }, [fields.maxNumberOfColumns, windowResizeTime]);

    React.useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("init", setCanScroll);
        embla.on("select", setCanScroll);
        const onWindowResize = () => {
            setWindowResizeTime(Date.now());
        };

        window.addEventListener("resize", onWindowResize);
        onWindowResize();

        return () => {
            window.removeEventListener("resize", onWindowResize);
        };
    }, [embla]);

    React.useEffect(() => {
        if (!embla || fields.industriesCards.length === 0) {
            return;
        }

        const newSlidesToScroll = getSlidesToScroll();

        embla.reInit({
            align: "center",
            slidesToScroll: newSlidesToScroll,
            watchDrag: draggable,
        });
        setCanScroll();
    }, [embla, slidesToScroll, draggable, fields.industriesCards]);

    return (
        <StyledWrapper {...styles.topMainContainer}>
            <StyledWrapper {...styles.titleContainerStyles}>
                <Typography as="h3" {...styles.categoryNameTitleStyle}>
                    {fields.mainTitle}
                </Typography>
                <StyledWrapper {...styles.linkContainerStyles}>
                    <Link href={titleLink.url} {...styles.linkStyles}>
                        {fields.seeAllLinkLabel}
                        <Icon src={ArrowRightLongIcon} {...styles.rightIconStyles} />
                    </Link>
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper
                css={css`
                    margin-top: 32px;
                `}
            >
                <Hidden below="md">
                    <GridContainer {...styles.mainContainer}>
                        {fields.industriesCards?.length > slidesToScroll && canScrollPrev && (
                            <GridItem {...styles.prevArrowGridItem}>
                                <Button
                                    {...styles.prevArrowButton}
                                    onClick={() => embla && embla.scrollPrev()}
                                    disabled={!canScrollPrev}
                                    data-test-selector="prevBtn"
                                >
                                    <ButtonIcon src={ChevronLeft} />
                                </Button>
                            </GridItem>
                        )}

                        <GridItem {...styles.carouselGridItem}>
                            <StyledWrapper {...styles.carouselWrapper}>
                                <StyledWrapper {...styles.carouselContainer} ref={emblaRef}>
                                    <StyledWrapper {...styles.carouselSlidesContainer} data-test-selector="slides">
                                        {fields.industriesCards?.map((item: any, index: number) => {
                                            return (
                                                <StyledWrapper
                                                    {...styles.carouselSlide}
                                                    // eslint-disable-next-line react/no-array-index-key
                                                    key={index}
                                                    style={{ width: `calc(100% / ${slidesToScroll})` }}
                                                >
                                                    <StyledWrapper
                                                        {...styles.carouselSlideInner}
                                                        data-test-selector="carousel_slide"
                                                    >
                                                        <StyledWrapper {...styles.imageContainer}>
                                                            <LazyImage
                                                                src={item.fields.industryImg}
                                                                {...styles.image}
                                                                altText={item.fields.industryLinkLabel}
                                                            />
                                                        </StyledWrapper>
                                                        <StyledWrapper {...styles.subCategoryContentContainer}>
                                                            <Link
                                                                href={industriesLinks[index].url}
                                                                {...styles.readMoreStyles}
                                                            >
                                                                {item.fields.industryLinkLabel}
                                                            </Link>
                                                            <Typography {...styles.subcategoryNameStyles}>
                                                                {item.fields.industryTitle}
                                                            </Typography>
                                                        </StyledWrapper>
                                                    </StyledWrapper>
                                                </StyledWrapper>
                                            );
                                        })}
                                    </StyledWrapper>
                                </StyledWrapper>
                            </StyledWrapper>
                        </GridItem>
                        {fields.industriesCards?.length > slidesToScroll && canScrollNext && (
                            <GridItem {...styles.nextArrowGridItem}>
                                <Button
                                    {...styles.nextArrowButton}
                                    onClick={() => embla && embla.scrollNext()}
                                    disabled={!canScrollNext}
                                    data-test-selector="nextBtn"
                                >
                                    <ButtonIcon src={ChevronRight} />
                                </Button>
                            </GridItem>
                        )}
                    </GridContainer>
                </Hidden>

                <Hidden above="sm">
                    <GridContainer {...styles.mainContainer}>
                        <StyledWrapper {...styles.carouselWrapper}>
                            <StyledWrapper {...styles.carouselContainer}>
                                <StyledWrapper {...styles.carouselSlidesContainer} data-test-selector="mobileStatic">
                                    {fields.industriesCards?.slice(0, 3)?.map((item: any, index: number) => {
                                        return (
                                            <StyledWrapper {...styles.carouselSlide} key={index}>
                                                <StyledWrapper
                                                    {...styles.carouselSlideInner}
                                                    data-test-selector="carousel_slide"
                                                >
                                                    <StyledWrapper {...styles.imageContainer}>
                                                        <LazyImage src={item.fields.industryImg} {...styles.image} altText={item.fields.industryLinkLabel} />
                                                    </StyledWrapper>
                                                    <StyledWrapper {...styles.subCategoryContentContainer}>
                                                        <Link
                                                            href={industriesLinks[index].url}
                                                            {...styles.readMoreStyles}
                                                        >
                                                            {item.fields.industryLinkLabel}
                                                        </Link>
                                                        <Typography {...styles.subcategoryNameStyles}>
                                                            {item.fields.industryTitle}
                                                        </Typography>
                                                    </StyledWrapper>
                                                </StyledWrapper>
                                            </StyledWrapper>
                                        );
                                    })}
                                </StyledWrapper>
                            </StyledWrapper>
                        </StyledWrapper>
                    </GridContainer>
                </Hidden>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withIsInShell(withTheme(CENHomePageBlogSection)),
    definition: {
        displayName: " CEN HomePage Blog",
        group: "Common",
        fieldDefinitions: [
            {
                name: fields.maxNumberOfColumns,
                displayName: "Max Number Of Columns",
                editorTemplate: "IntegerField",
                min: 1,
                max: 6,
                defaultValue: 6,
                fieldType: "General",
                sortOrder: 8,
                isVisible: item => false,
            },

            {
                name: fields.mainTitle,
                fieldType: "General",
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                name: fields.seeAllLinkLabel,
                editorTemplate: "TextField",
                displayName: "Link Label",
                fieldType: "General",
                defaultValue: "",
            },
            {
                name: fields.seeAllLink,
                fieldType: "General",
                displayName: "Redirection Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
            },
            {
                name: fields.industriesCards,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.industryTitle;
                },
                defaultValue: [],
                fieldType: "General",
                displayName: "Add list",
                fieldDefinitions: [
                    {
                        name: fields.industryImg,
                        editorTemplate: "ImagePickerField",
                        displayName: "Image",
                        defaultValue: "",
                    },
                    {
                        name: fields.industryTitle,
                        editorTemplate: "TextField",
                        displayName: "Title",
                        defaultValue: "",
                    },
                    {
                        name: fields.industryDescription,
                        editorTemplate: "RichTextField",
                        defaultValue: "",
                        displayName: "Description",
                    },

                    {
                        name: fields.industryLinkLabel,
                        editorTemplate: "TextField",
                        displayName: "Link Label",
                        defaultValue: "",
                    },

                    {
                        name: "industryLink",
                        displayName: "Redirection Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
